'use client';

import { FC, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingButton } from '@/components/loading-button';
import { useSubmitStatus } from '@/hooks/useSubmitStatus';
import { sendInvoiceToClient } from '@/actions/invoice/send-invoice';
import { toast } from 'sonner';
import { useTranslations } from '@/hooks/useTranslations';

interface SendInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  clientEmail?: string | null;
}

export const SendInvoiceDialog: FC<SendInvoiceDialogProps> = ({
  open,
  onOpenChange,
  invoiceId,
  clientEmail,
}) => {
  const [email, setEmail] = useState(clientEmail || '');
  const { status, wrap } = useSubmitStatus();
  const t = useTranslations('InvoiceOverview');

  const handleSend = () => {
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }

    wrap(async () => {
      const result = await sendInvoiceToClient(invoiceId, email);

      if (result.error) {
        toast.error(result.error);
        throw new Error(result.error);
      }

      toast.success(result.success);
      onOpenChange(false);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('sendInvoiceTitle')}</DialogTitle>
          <DialogDescription>{t('sendInvoiceDescription')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t('recipientEmail')}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="client@example.com"
            />
          </div>
        </div>

        <DialogFooter>
          <LoadingButton
            type="button"
            status={status}
            idleText={t('sendToClient')}
            loadingText={t('sending')}
            successText={t('sent')}
            errorText="Failed"
            onClick={handleSend}
            isFull={false}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
